import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useMagazineModal } from "../components/Nav";
import { ArrowRight, Download } from "lucide-react";

/**
 * Public, shareable landing page for the Nocturne magazine.
 * URL:  https://thegoodbandit.com/magazine
 * Auto-opens the subscribe modal on load and offers a clean cinematic frame
 * for social/email/QR-code sharing.
 */
export default function MagazineLanding() {
  const { open } = useMagazineModal();
  const nav = useNavigate();

  useEffect(() => {
    // Auto-open the subscribe modal shortly after the hero animates in.
    const t = setTimeout(() => open("magazine-share"), 1200);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <main data-testid="magazine-landing" className="bg-black min-h-screen overflow-hidden noise relative pt-32 pb-24">
      {/* background art */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=2400&q=90')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />

      <section className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 items-center min-h-[70vh]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-7"
        >
          <p className="overline mb-6" data-testid="magazine-landing-eyebrow">
            Good Bandit · Nocturne · Issue 01
          </p>
          <h1 className="font-display text-6xl md:text-8xl text-white leading-[0.92]">
            The Art of
            <br />
            <span className="italic text-[var(--gb-gold)]">Control.</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-xl mt-10 leading-relaxed">
            An editorial experience where cinema, photography and publishing
            converge. Enter your details to read the inaugural issue.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-12">
            <button
              onClick={() => open("magazine-share")}
              className="gb-btn-primary"
              data-testid="magazine-landing-subscribe"
            >
              Download Latest Magazine <ArrowRight size={14} />
            </button>
            <button
              onClick={() => nav("/nocturne")}
              className="gb-btn-outline"
              data-testid="magazine-landing-explore"
            >
              Explore the Experience <ArrowRight size={14} />
            </button>
          </div>

          <p className="kbd text-white/40 mt-10">
            SHARE · thegoodbandit.com/magazine
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-5 md:col-start-8"
        >
          <div className="aspect-[4/5] w-full max-w-md ml-auto relative overflow-hidden noise">
            <img
              src="https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=1200&q=90"
              alt="Nocturne Issue 01 cover"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6">
              <p className="kbd text-white">NOCTURNE · ISSUE 01</p>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-display text-2xl text-white italic">
                The Art of Control
              </p>
              <div className="gb-divider my-3" />
              <p className="kbd text-white/60">A GOOD BANDIT PUBLICATION</p>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mt-16 flex items-center gap-4 text-white/40">
        <Download size={14} />
        <p className="kbd">SUBSCRIBER-ONLY DOWNLOAD · CASL / GDPR FRIENDLY</p>
      </div>
    </main>
  );
}
